import React, { FunctionComponent } from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { Box } from '@rebass/emotion';

import Link from '@app/components/Link';
import useMediaQuery from '@app/helpers/useMediaQuery';
import { screenExtraSmall } from '@app/styles/theme/breakpoints';

import FormattedMoney from '../../../components/FormattedMoney';
import { OrderHistoryTable, boxStyles } from './OrderHistory.styles';
import messages from './OrderHistoryItems.messages';
import { OrderHistoryQuery_magento2_customer_globalOrders } from './__generated__/OrderHistoryQuery';

const createOrderDetailsUrl = (orderNumber: string) =>
  `/:language/account/orders/${orderNumber}/`;

const OrderHistoryItems: FunctionComponent<
  Pick<OrderHistoryQuery_magento2_customer_globalOrders, 'items'>
> = ({ items }) => {
  const isMobile = useMediaQuery(`(max-width: ${screenExtraSmall})`, {
    ssrMatch: true,
  });

  if (isMobile) {
    return (
      <>
        {items?.map((order) =>
          order ? (
            <Box css={boxStyles} py={15} key={order.id}>
              <p>
                <b>
                  <FormattedMessage {...messages.orderDate} />:{' '}
                </b>
                <FormattedDate
                  year="2-digit"
                  month="2-digit"
                  day="2-digit"
                  value={new Date(order.orderDate)}
                />
              </p>
              <p>
                <b>
                  <FormattedMessage {...messages.orderProducts} />:{' '}
                </b>
                {order.items?.map((orderItem, index) => (
                  <span key={index}>
                    {orderItem?.productName} ({orderItem?.quantityOrdered})
                    {(order.items?.length ?? 0) - 1 === index ? '' : ', '}
                  </span>
                ))}
              </p>
              <p>
                <b>
                  <FormattedMessage {...messages.orderReference} />:{' '}
                </b>
                <b>{order?.number}</b>
              </p>
              <p>
                <FormattedMoney
                  value={order.total?.grandTotal.value}
                  currency={order.total?.grandTotal?.currency}
                />
              </p>
              <p>
                <Link to={createOrderDetailsUrl(order.number)}>
                  <FormattedMessage {...messages.orderViewDetails} />
                </Link>
              </p>
            </Box>
          ) : null
        )}
      </>
    );
  }

  return (
    <OrderHistoryTable>
      <thead>
        <tr>
          <th>
            <FormattedMessage {...messages.orderDate} />
          </th>
          <th data-table="order-products">
            <FormattedMessage {...messages.orderProducts} />
          </th>
          <th>
            <FormattedMessage {...messages.orderReference} />
          </th>
          <th>
            <FormattedMessage {...messages.orderTotal} />
          </th>
          <th />
        </tr>
      </thead>

      <tbody>
        {items?.map((order) =>
          order ? (
            <tr key={order.id}>
              <td>
                <FormattedDate
                  year="2-digit"
                  month="2-digit"
                  day="2-digit"
                  value={new Date(order.orderDate)}
                />
              </td>

              <td data-table="order-products">
                {order.items?.map((orderItem, index) => (
                  <span key={index}>
                    {orderItem?.productName} ({orderItem?.quantityOrdered})
                    {(order.items?.length ?? 0) - 1 === index ? '' : ', '}
                  </span>
                ))}
              </td>

              <td>
                <b>{order?.number}</b>
              </td>

              <td>
                <FormattedMoney
                  value={order.total?.grandTotal.value}
                  currency={order.total?.grandTotal?.currency}
                />
              </td>

              <td>
                <Link to={createOrderDetailsUrl(order.number)}>
                  <FormattedMessage {...messages.orderViewDetails} />
                </Link>
              </td>
            </tr>
          ) : null
        )}
      </tbody>
    </OrderHistoryTable>
  );
};

export default OrderHistoryItems;
